import { useState } from "react";
import { useInputChannelConfig } from "../hooks/useInputChannelConfig";
import { useDirectoryImport } from "../hooks/useDirectoryImport";
import { useJobTypes } from "../hooks/useJobTypes";
import { ApiError } from "@/lib/api-client";

interface InvalidPathError {
  error: "invalid_paths";
  // F-039 — server now validates SYNTACTICALLY (the watcher runs in a separate
  // container, so the api can't stat the path's existence). Reasons are
  // "empty" / "not_absolute"; existence is verified by the worker at ingest.
  invalidPaths: Array<{ path: string; reason: "empty" | "not_absolute" }>;
}

function formatSaveError(e: unknown): string {
  if (e instanceof ApiError && e.status === 400) {
    const body = e.body as Partial<InvalidPathError> | null;
    if (body?.error === "invalid_paths" && Array.isArray(body.invalidPaths)) {
      const lines = body.invalidPaths.map((p) =>
        p.reason === "not_absolute"
          ? `"${p.path}" must be an absolute path (start with /)`
          : `A base path cannot be empty`,
      );
      return `Path validation failed:\n${lines.join("\n")}`;
    }
  }
  return e instanceof Error ? e.message : "Failed to save";
}

export function DirectoryImportPanel() {
  const { config, isLoading: configLoading, save: saveConfig } = useInputChannelConfig();
  const { importDirectory, isImporting, error: importError, lastResult } = useDirectoryImport();
  const { items: jobTypes } = useJobTypes();

  const [newPath, setNewPath] = useState("");
  const [importPath, setImportPath] = useState("");
  const [selectedJobType, setSelectedJobType] = useState("");
  const [manifestMode, setManifestMode] = useState<"none" | "optional" | "required">("none");
  const [manifestFilename, setManifestFilename] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleAddPath = async () => {
    if (!newPath.trim()) return;
    const current = config?.allowedBasePaths ?? [];
    if (current.includes(newPath.trim())) return;
    setSaveError(null);
    try {
      await saveConfig([...current, newPath.trim()]);
      setNewPath("");
    } catch (e) {
      setSaveError(formatSaveError(e));
    }
  };

  const handleRemovePath = async (path: string) => {
    const current = config?.allowedBasePaths ?? [];
    setSaveError(null);
    try {
      await saveConfig(current.filter((p) => p !== path));
    } catch (e) {
      setSaveError(formatSaveError(e));
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importPath.trim()) return;
    const trimmedFilename = manifestFilename.trim();
    try {
      await importDirectory({
        path: importPath.trim(),
        jobTypeId: selectedJobType || undefined,
        manifestMode: manifestMode === "none" ? undefined : manifestMode,
        manifestFilename: manifestMode !== "none" && trimmedFilename ? trimmedFilename : undefined,
      });
    } catch {
      // error surfaced via importError
    }
  };

  if (configLoading) {
    return <div className="admin-loading"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="panel-content">
      {/* Allowed Base Paths */}
      <div className="rb-section">
        <h3 className="rb-section-title">Allowed Base Paths</h3>
        <p className="rb-section-desc">
          Only directories under these paths may be imported. Restrict to trusted network shares or local folders.
        </p>
        <p className="rb-hint" style={{ marginBottom: 8 }}>
          On Railway, directory imports and hot folders require a <strong>shared persistent
          volume mounted at the same path on both the API and worker services</strong> — the
          worker (not the API) reads these directories. For cloud deployments without a shared
          volume, use the <strong>S3 / R2 Import</strong> tab instead.
        </p>

        {saveError && <div className="admin-error">{saveError}</div>}

        <div className="path-list">
          {(config?.allowedBasePaths ?? []).length === 0 && (
            <div className="tab-placeholder">
              <span className="tab-placeholder-text">
                No base paths added yet. Add at least one trusted directory below to
                enable directory imports.
              </span>
            </div>
          )}
          {(config?.allowedBasePaths ?? []).map((path) => (
            <div key={path} className="path-row">
              <span className="path-value">{path}</span>
              <button
                className="rb-btn rb-btn-danger"
                onClick={() => handleRemovePath(path)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div className="rb-row" style={{ marginTop: 12 }}>
          <input
            className="rb-input"
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            placeholder="/mnt/shares/documents"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddPath(); } }}
          />
          <button className="rb-btn rb-btn-save" onClick={handleAddPath} disabled={!newPath.trim()}>
            Add Path
          </button>
        </div>
      </div>

      {/* Manual Import */}
      <div className="rb-section" style={{ marginTop: 24 }}>
        <h3 className="rb-section-title">Manual Directory Import</h3>
        <p className="rb-section-desc">
          Trigger a one-time import from a directory. The path must be under an allowed base path.
        </p>

        <form onSubmit={handleImport}>
          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Directory Path</label>
            <input
              className="rb-input"
              value={importPath}
              onChange={(e) => setImportPath(e.target.value)}
              placeholder="/mnt/shares/documents/2026-04-09"
              required
            />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Job Type</label>
            <select
              className="rb-select"
              value={selectedJobType}
              onChange={(e) => setSelectedJobType(e.target.value)}
            >
              <option value="">Auto-detect</option>
              {jobTypes
                .filter((jt) => jt.isActive && jt.currentPublishedVersionId)
                .map((jt) => (
                  <option key={jt.id} value={jt.id}>{jt.name}</option>
                ))}
            </select>
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Manifest parsing</label>
            <select
              className="rb-select"
              value={manifestMode}
              onChange={(e) => setManifestMode(e.target.value as "none" | "optional" | "required")}
            >
              <option value="none">None</option>
              <option value="optional">Optional</option>
              <option value="required">Required</option>
            </select>
          </div>

          {manifestMode !== "none" && (
            <div className="rb-row" style={{ marginBottom: 16 }}>
              <label className="rb-label">Manifest Filename</label>
              <input
                className="rb-input"
                value={manifestFilename}
                onChange={(e) => setManifestFilename(e.target.value)}
                placeholder="manifest.xml"
              />
            </div>
          )}

          {importError && <div className="admin-error" style={{ marginBottom: 10 }}>{importError}</div>}

          {lastResult && (
            <div className="import-result">
              <span className="import-result-ok">Import complete:</span>{" "}
              {lastResult.fileCount} files → batch "{lastResult.batchName}"
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="submit" className="rb-btn rb-btn-save" disabled={isImporting || !importPath.trim()}>
              {isImporting ? "Importing…" : "Import"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
